// R5, R6, R7 (docs/build-briefs/R5-*.md, R6-*.md, R7-*.md; design/mockups/roles-r5-*.html to
// roles-r7-*.html; DEC-313). The role management dialogs (Options, Rename, Make a copy, Switch off with
// Move all), the save confirmation for a staged role edit, the add-a-role preview, and the Edit
// Employee Access readout with its add-an-exception dialog.
//
// Admin surface: a person on a computer or a tablet. Every control is 44px or taller, nothing depends
// on hover, and every dialog has a close button (Escape is a bonus, never the only exit). Text is set
// with textContent only (names and task labels are user entered). No permission id is ever shown.
//
// The dialogs are siblings inside the two backdrops ui/src/admin.ts already owns
// (#admin-role-edit-scrim and #admin-user-edit-scrim), one visible at a time, because
// check:chrome-ratchet caps the number of .scrim elements. This module owns no scrim of its own.
// Whatever the dialogs need from the Settings screen (the roles and people already loaded, a reload,
// the toast, the body scroll lock) comes in through initRolesManagement, so nothing here reaches into
// admin.ts and the teardown (resetRolesManagement, dismissRolesManagementSheets) is one call each.

import {
  copyTenantRole,
  createTenantRole,
  getRoleSwitchOffPlan,
  previewAddRoleToUser,
  previewRolePermissionChanges,
  renameTenantRole,
  retireTenantRole,
  saveRolePermissions,
  type AdminUserDto,
  type PermissionGrant,
  type RoleSwitchOffPlanDto,
  type TenantRoleDto,
  type UserPermissionRowDto,
} from "./api.ts";
import {
  PEOPLE_CHIPS_VISIBLE,
  CHANGE_SYMBOLS,
  accessCounts,
  addRoleIntro,
  areaSummaries,
  blockedBannerText,
  changeLine,
  copyNameSuggestions,
  defaultCopyName,
  exceptionChoiceBlock,
  exceptionChoices,
  exceptionRows,
  exceptionSaveReady,
  firstName,
  gainLine,
  grantWord,
  holderNote,
  holdersSentence,
  initials,
  moveFooterText,
  peopleCountText,
  peopleLeftToMove,
  personalSettingsNote,
  personalWinsNote,
  readyBannerText,
  roleNamesFor,
  roleSaysText,
  saveBannerText,
  saveButtonText,
  sourceBadge,
  switchOffHint,
} from "./rolesManagementModel.ts";
import {
  noMatchText,
  PERMISSION_DESCRIPTIONS,
  permissionCountText,
  searchPermissionSections,
  splitForHighlight,
} from "./permissionSections.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
import { describeSetting } from "./settingsDescription.ts";
import type { Session } from "./session.ts";
import { sortByName } from "./nameSort.ts";

export interface RolesManagementContext {
  session(): Session | undefined;
  roles(): readonly TenantRoleDto[];
  users(): readonly AdminUserDto[];
  /** Re-reads the people and the roles and redraws the Roles list. */
  reload(): Promise<void>;
  openEditor(role: string): Promise<void>;
  /** MGRROLE1 (DEC-599): false for a person who may only SEE what roles can do (a permission read, never a role name). */
  mayChangeRoles(): boolean;
  toast(message: string, variant: "success" | "danger"): void;
  lockScroll(): void;
  unlockScroll(): void;
}

interface Host {
  scrimId: string;
  /** The sheet the dialogs return to (the role editor, or the Edit Employee sheet). */
  backSheetId: string;
}

const ROLE_HOST: Host = { scrimId: "admin-role-edit-scrim", backSheetId: "admin-role-edit-sheet" };
const USER_HOST: Host = { scrimId: "admin-user-edit-scrim", backSheetId: "admin-user-edit-sheet" };

let context: RolesManagementContext | undefined;
/** True while a dialog from this module is the thing holding the body scroll lock. */
const ownsLock = new Map<string, boolean>();
/** Bumped by every teardown, so an answer that arrives after sign-out is dropped. */
let generation = 0;

export function initRolesManagement(next: RolesManagementContext): void {
  context = next;
}

function ctx(): RolesManagementContext {
  if (context === undefined) {
    throw new Error("rolesManagement used before initRolesManagement");
  }
  return context;
}

// ---------------------------------------------------------------------------------------------
// Small DOM helpers (text only)
// ---------------------------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined && className !== "") element.className = className;
  if (testId !== undefined) element.dataset["testid"] = testId;
  if (text !== undefined) element.textContent = text;
  return element;
}

function button(
  label: string,
  className: string,
  testId: string,
  onClick: () => void,
): HTMLButtonElement {
  const b = el("button", className, testId, label);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

function callout(kind: "" | "warn" | "bad" | "good", icon: string, bold: string, rest: string) {
  const box = el("div", kind === "" ? "rl-callout" : `rl-callout rl-${kind}`);
  box.append(el("span", "", undefined, icon));
  const text = el("span");
  if (bold !== "") text.append(el("b", "", undefined, bold));
  text.append(document.createTextNode(rest));
  box.append(text);
  return box;
}

function section(text: string): HTMLElement {
  return el("p", "rl-sec", undefined, text);
}

function person(displayName: string, small: boolean): HTMLElement {
  return el("span", small ? "rl-av rl-av-sm" : "rl-av", undefined, initials(displayName));
}

function labelOf(role: string): string {
  return (
    ctx()
      .roles()
      .find((r) => r.role === role)?.label ?? role
  );
}

function holdersOf(role: string): AdminUserDto[] {
  return ctx()
    .users()
    .filter((user) => user.active && (user.roles ?? [user.role]).includes(role));
}

function failureMessage(
  result: { body: { message?: string | undefined; error?: string | undefined } },
  fallback: string,
): string {
  return capitalizeServerMessage(result.body.message ?? result.body.error ?? fallback);
}

// ---------------------------------------------------------------------------------------------
// Presenting a dialog inside one of the two shared backdrops
// ---------------------------------------------------------------------------------------------

function scrimOf(host: Host): HTMLElement | null {
  return document.getElementById(host.scrimId);
}

function hideAllSheets(host: Host): void {
  const scrim = scrimOf(host);
  if (scrim === null) return;
  for (const child of Array.from(scrim.children)) {
    if (child instanceof HTMLElement) child.hidden = true;
  }
}

function present(host: Host, sheet: HTMLElement): void {
  const scrim = scrimOf(host);
  if (scrim === null) return;
  hideAllSheets(host);
  sheet.hidden = false;
  scrim.classList.remove("rl-drawer");
  scrim.classList.add("rl-centered");
  if (scrim.hidden) {
    scrim.hidden = false;
    ctx().lockScroll();
    ownsLock.set(host.scrimId, true);
  }
}

/** Closes the dialog. With `backToSheet` the sheet it came from is shown again (the editor with its
 * staged changes, the Edit Employee sheet); otherwise the backdrop closes and the lock is released. */
function dismiss(host: Host, sheet: HTMLElement, backToSheet: boolean): void {
  const scrim = scrimOf(host);
  sheet.hidden = true;
  if (scrim === null) return;
  scrim.classList.remove("rl-centered");
  if (backToSheet) {
    const back = document.getElementById(host.backSheetId);
    if (back !== null) {
      back.hidden = false;
      scrim.classList.add("rl-drawer");
      return;
    }
  }
  scrim.hidden = true;
  scrim.classList.remove("rl-drawer");
  if (ownsLock.get(host.scrimId) === true) {
    ownsLock.set(host.scrimId, false);
    ctx().unlockScroll();
  }
}

interface Dialog {
  sheet: HTMLElement;
  body: HTMLElement;
  foot: HTMLElement;
}

function buildDialog(
  host: Host,
  id: string,
  eyebrow: string,
  title: string,
  width: number,
  onClose: () => void,
): Dialog {
  const scrim = scrimOf(host);
  if (scrim === null) {
    throw new Error(`expected #${host.scrimId}`);
  }
  let sheet = document.getElementById(id);
  if (sheet === null) {
    sheet = el("div", "sheet rl-dlg");
    sheet.id = id;
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.hidden = true;
    scrim.append(sheet);
  }
  sheet.dataset["testid"] = id;
  sheet.style.setProperty("--rl-dw", `${width}px`);
  sheet.setAttribute("aria-label", title);
  sheet.replaceChildren();

  const head = el("div", "rl-dh");
  const text = el("div", "rl-dh-text");
  text.append(el("div", "rl-ey", undefined, eyebrow));
  text.append(el("h2", "rl-title", `${id}-title`, title));
  head.append(text);
  const close = el("button", "rl-x", `${id}-close`, "×");
  close.type = "button";
  close.setAttribute("aria-label", "Close");
  close.addEventListener("click", onClose);
  head.append(close);

  const body = el("div", "rl-db");
  const foot = el("div", "rl-df");
  sheet.append(head, body, foot);
  return { sheet: sheet, body, foot };
}

function cancelButton(id: string, label: string, onClick: () => void): HTMLButtonElement {
  return button(label, "rl-btn", `${id}-cancel`, onClick);
}

// ---------------------------------------------------------------------------------------------
// R5: Options
// ---------------------------------------------------------------------------------------------

function optionRow(
  testId: string,
  title: string,
  subtitle: string,
  onClick: () => void,
  danger: boolean,
): HTMLButtonElement {
  const row = el("button", danger ? "rl-opt rl-opt-danger" : "rl-opt", testId);
  row.type = "button";
  const text = el("span", "rl-opt-text");
  text.append(el("b", "", undefined, title), el("small", "", undefined, subtitle));
  row.append(text, el("span", "rl-opt-go", undefined, "›"));
  row.addEventListener("click", onClick);
  return row;
}

export function openRoleOptions(role: TenantRoleDto): void {
  const id = "admin-role-options-sheet";
  const close = (): void => dismiss(ROLE_HOST, dialog.sheet, false);
  const dialog = buildDialog(ROLE_HOST, id, "Role", role.label, 460, close);
  const holders = holdersOf(role.role).length;
  dialog.body.append(el("p", "rl-muted", `${id}-holders`, holdersSentence(holders)));
  const list = el("div", "rl-opt-list");
  list.append(
    optionRow(
      "admin-role-option-edit",
      ctx().mayChangeRoles() ? "Open and edit what it can do" : "See what it can do",
      ctx().mayChangeRoles()
        ? "Tasks this role is allowed to do"
        : "Read only. Only an Admin can change what a role can do.",
      () => {
        close();
        void ctx().openEditor(role.role);
      },
      false,
    ),
  );
  if (!role.isShipped && ctx().mayChangeRoles()) {
    list.append(
      optionRow(
        "admin-role-option-rename",
        "Rename",
        "Change the name people see",
        () => openRenameDialog(role),
        false,
      ),
    );
  }
  if (ctx().mayChangeRoles())
    list.append(
      optionRow(
        "admin-role-option-copy",
        "Make a copy",
        "Start a new role with the same tasks",
        () => openCopyDialog(role),
        false,
      ),
    );
  if (!role.isSystem && ctx().mayChangeRoles()) {
    list.append(
      optionRow(
        "admin-role-option-switchoff",
        "Switch off",
        switchOffHint(holders),
        () => void openSwitchOffDialog(role),
        true,
      ),
    );
  }
  dialog.body.append(list);
  const notes: string[] = [];
  if (role.isShipped) notes.push("Built-in roles cannot be renamed. Make a copy instead.");
  if (role.isSystem) notes.push("This role cannot be switched off.");
  if (notes.length > 0) {
    dialog.body.append(el("p", "rl-muted", `${id}-note`, notes.join(" ")));
  }
  dialog.foot.hidden = true;
  present(ROLE_HOST, dialog.sheet);
}

// ---------------------------------------------------------------------------------------------
// R5: Rename
// ---------------------------------------------------------------------------------------------

function openRenameDialog(role: TenantRoleDto): void {
  const id = "admin-role-rename-sheet";
  const close = (): void => dismiss(ROLE_HOST, dialog.sheet, false);
  const dialog = buildDialog(ROLE_HOST, id, "Rename role", `Rename ${role.label}`, 600, close);
  const renameHelp = el(
    "p",
    "",
    undefined,
    "Only the name changes. Past records show the new name.",
  );
  renameHelp.id = `${id}-help`;
  dialog.body.append(renameHelp);
  const field = el("div");
  const labelEl = el("label", "rl-field-label", undefined, "New name");
  const input = el("input", "rl-input", `${id}-input`);
  input.type = "text";
  input.maxLength = 80;
  input.value = role.label;
  input.inputMode = "text";
  input.id = `${id}-input`;
  describeSetting(input, renameHelp.id);
  labelEl.htmlFor = input.id;
  const error = el("div", "rl-err-text", `${id}-error`);
  error.hidden = true;
  const aside = el("div");
  field.append(labelEl, input, error, aside);
  dialog.body.append(field);

  const save = button("Save name", "rl-btn rl-primary", `${id}-save`, () => void submit());
  const refresh = (): void => {
    const typed = input.value.trim().replace(/\s+/g, " ");
    save.disabled = typed.length === 0 || typed === role.label;
  };
  input.addEventListener("input", () => {
    input.classList.remove("rl-err");
    error.hidden = true;
    aside.replaceChildren();
    refresh();
  });
  dialog.foot.append(cancelButton(id, "Cancel", close), save);
  refresh();

  async function submit(): Promise<void> {
    const s = ctx().session();
    if (s === undefined) return;
    const gen = generation;
    save.disabled = true;
    const result = await renameTenantRole(s.apiBaseUrl, s.tenantId, role.role, input.value);
    if (gen !== generation) return;
    if (result.status !== 200) {
      input.classList.add("rl-err");
      error.textContent = failureMessage(result, "Could not rename that role.");
      error.hidden = false;
      aside.replaceChildren();
      if (result.body.error === "role_name_taken" && result.body.takenByActive === false) {
        const owner = ctx()
          .roles()
          .find((r) => r.role === result.body.takenByRole);
        if (owner !== undefined) {
          aside.append(
            callout(
              "",
              "ⓘ",
              "",
              `Want that old role back? Switch ${owner.label} back on instead. It returns with the tasks it had and nobody in it.`,
            ),
            button(
              `Switch ${owner.label} back on`,
              "rl-btn rl-small",
              `${id}-switch-back-on`,
              () => void switchBackOn(owner, () => dismiss(ROLE_HOST, dialog.sheet, false)),
            ),
          );
        }
      }
      // Save stays off after a refusal until the name is edited (the input handler re-checks it).
      return;
    }
    dismiss(ROLE_HOST, dialog.sheet, false);
    ctx().toast(`Renamed to ${result.body.role.label}.`, "success");
    await ctx().reload();
  }
  present(ROLE_HOST, dialog.sheet);
  input.focus();
  input.select();
}

/** Switching a role back on is what creating it again by name does (DEC-033). */
export async function switchBackOn(role: TenantRoleDto, afterwards?: () => void): Promise<void> {
  const s = ctx().session();
  if (s === undefined) return;
  const gen = generation;
  const result = await createTenantRole(s.apiBaseUrl, s.tenantId, role.role, role.label);
  if (gen !== generation) return;
  if (result.status !== 200 && result.status !== 201) {
    ctx().toast(failureMessage(result, "Could not switch that role back on."), "danger");
    return;
  }
  afterwards?.();
  ctx().toast(`${role.label} is switched back on. Nobody has it yet.`, "success");
  await ctx().reload();
}

// ---------------------------------------------------------------------------------------------
// R5: Make a copy
// ---------------------------------------------------------------------------------------------

function openCopyDialog(role: TenantRoleDto): void {
  const id = "admin-role-copy-sheet";
  const close = (): void => dismiss(ROLE_HOST, dialog.sheet, false);
  const dialog = buildDialog(ROLE_HOST, id, "Make a copy", defaultCopyName(role.label), 600, close);
  const copyHelp = el(
    "p",
    "",
    undefined,
    `Starts with the same tasks as ${role.label}. Nobody is moved into it.`,
  );
  copyHelp.id = `${id}-help`;
  dialog.body.append(copyHelp);
  const field = el("div");
  const labelEl = el("label", "rl-field-label", undefined, "Name for the new role");
  const input = el("input", "rl-input", `${id}-input`);
  input.type = "text";
  input.maxLength = 80;
  input.inputMode = "text";
  input.id = `${id}-input`;
  describeSetting(input, copyHelp.id);
  input.value = defaultCopyName(role.label);
  labelEl.htmlFor = input.id;
  const error = el("div", "rl-err-text", `${id}-error`);
  error.hidden = true;
  field.append(labelEl, input, error);
  dialog.body.append(field);

  dialog.body.append(section("Or tap a name"));
  const chips = el("div", "rl-chips");
  for (const suggestion of copyNameSuggestions(
    role.label,
    ctx()
      .roles()
      .map((r) => r.label),
  )) {
    chips.append(
      button(suggestion, "rl-chip", `${id}-suggestion`, () => {
        input.value = suggestion;
        input.dispatchEvent(new Event("input"));
      }),
    );
  }
  dialog.body.append(chips);
  const refusal = el("div", "", `${id}-refusal`);
  refusal.hidden = true;
  refusal.style.marginTop = "var(--space-4)";
  dialog.body.append(refusal);
  const nameRule = el(
    "p",
    "rl-muted",
    undefined,
    "Names must be different from every role in this shop, even switched-off ones.",
  );
  nameRule.style.marginTop = "var(--space-3)";
  dialog.body.append(nameRule);

  const make = button("Make the copy", "rl-btn rl-primary", `${id}-make`, () => void submit());
  /** The name is invalid while blank or while another role already has it, switched off or not. */
  const refresh = (): void => {
    const typed = input.value.trim().replace(/\s+/g, " ").toLowerCase();
    const owner = ctx()
      .roles()
      .find((r) => r.label.trim().replace(/\s+/g, " ").toLowerCase() === typed);
    if (typed.length > 0 && owner !== undefined) {
      input.classList.add("rl-err");
      error.textContent = `${owner.label} is already a role. Pick a different name.`;
      error.hidden = false;
    }
    make.disabled = typed.length === 0 || owner !== undefined;
  };
  input.addEventListener("input", () => {
    input.classList.remove("rl-err");
    error.hidden = true;
    refusal.hidden = true;
    refresh();
  });
  dialog.foot.append(cancelButton(id, "Cancel", close), make);
  refresh();

  async function submit(): Promise<void> {
    const s = ctx().session();
    if (s === undefined) return;
    const gen = generation;
    make.disabled = true;
    const result = await copyTenantRole(s.apiBaseUrl, s.tenantId, role.role, input.value);
    if (gen !== generation) return;
    if (result.status === 201) {
      dismiss(ROLE_HOST, dialog.sheet, false);
      ctx().toast(
        `${result.body.role.label} made. It has the same tasks as ${role.label} and nobody in it.`,
        "success",
      );
      await ctx().reload();
      return;
    }
    refusal.replaceChildren();
    if (result.body.error === "role_copy_over_grant") {
      // Refused, never trimmed (DEC-313 ruling 4): the copy is not made, and the tasks are named.
      const tasks = result.body.taskLabels ?? [];
      const box = callout(
        "bad",
        "\u26a0",
        "This copy cannot be made.",
        tasks.length === 0
          ? ` ${failureMessage(result, "You cannot give out everything this role can do.")}`
          : ` ${role.label} can do ${tasks.length === 1 ? "a task" : `${tasks.length} tasks`} you are not allowed to give out. Ask an Admin who has ${tasks.length === 1 ? "it" : "them"} to make the copy.`,
      );
      box.dataset["testid"] = `${id}-refusal-box`;
      refusal.append(box);
      if (tasks.length > 0) {
        refusal.append(section("Tasks you cannot give out"));
        const list = el("ul", "", `${id}-tasks`);
        list.style.margin = "0 0 var(--space-3)";
        list.style.paddingLeft = "var(--space-5)";
        list.style.maxHeight = "220px";
        list.style.overflowY = "auto";
        const drawList = (all: boolean): void => {
          list.replaceChildren();
          for (const task of all ? tasks : tasks.slice(0, 6)) {
            list.append(el("li", "", `${id}-task`, task));
          }
          if (!all && tasks.length > 6) {
            const more = el("li", "", undefined);
            more.style.listStyle = "none";
            more.style.paddingBottom = "var(--space-3)";
            more.append(
              button(`Show all ${tasks.length}`, "rl-btn rl-small", `${id}-show-all-tasks`, () => {
                drawList(true);
                list.scrollTop = 0;
              }),
            );
            list.append(more);
          }
        };
        drawList(false);
        refusal.append(list);
      }
      refusal.hidden = false;
    } else {
      input.classList.add("rl-err");
      error.textContent = failureMessage(result, "Could not make that copy.");
      error.hidden = false;
    }
    refresh();
    if (result.body.error === "role_copy_over_grant") make.disabled = true;
  }
  present(ROLE_HOST, dialog.sheet);
  input.focus();
  input.select();
}

// ---------------------------------------------------------------------------------------------
// R5: Switch off, blocked while anyone holds the role
// ---------------------------------------------------------------------------------------------

async function openSwitchOffDialog(role: TenantRoleDto): Promise<void> {
  const s = ctx().session();
  if (s === undefined) return;
  const gen = generation;
  const result = await getRoleSwitchOffPlan(s.apiBaseUrl, s.tenantId, role.role);
  if (gen !== generation) return;
  if (result.status !== 200) {
    ctx().toast(failureMessage(result, "Could not open that role."), "danger");
    return;
  }
  renderSwitchOff(role, result.body);
}

function renderSwitchOff(role: TenantRoleDto, plan: RoleSwitchOffPlanDto): void {
  const id = "admin-role-switchoff-sheet";
  const close = (): void => dismiss(ROLE_HOST, dialog.sheet, false);
  const dialog = buildDialog(
    ROLE_HOST,
    id,
    "Switch off a role",
    `Switch off ${plan.label}`,
    680,
    close,
  );
  /** userId to the role chosen for that person. */
  const picks: Record<string, string | undefined> = {};
  let allTo: string | undefined;
  let status = "";

  const everyoneGoesTo = (): string | undefined => {
    const chosen = new Set(plan.holders.map((h) => picks[h.userId]));
    return chosen.size === 1 && !chosen.has(undefined) ? [...chosen][0] : undefined;
  };
  const nameOf = (r: string): string =>
    plan.destinations.find((d) => d.role === r)?.label ?? labelOf(r);
  const confirm = button("Switch off", "rl-btn rl-danger", `${id}-confirm`, () => void submit());

  const draw = (): void => {
    dialog.body.replaceChildren();
    dialog.foot.replaceChildren();
    const left = peopleLeftToMove(plan.holders, picks);
    if (plan.holders.length > 0) {
      const banner =
        left > 0 ? blockedBannerText(plan.label, plan.holders.length) : readyBannerText();
      const box = callout(
        left > 0 ? "bad" : "good",
        left > 0 ? "⚠" : "✓",
        banner.bold,
        banner.rest,
      );
      box.dataset["testid"] = `${id}-banner`;
      dialog.body.append(box);

      dialog.body.append(section("Move everyone at once"));
      const chips = el("div", "rl-chips");
      for (const destination of sortByName(plan.destinations, (d) => d.label)) {
        if (!destination.assignable) continue;
        const chip = button(
          `All to ${destination.label}`,
          allTo === destination.role ? "rl-chip rl-on" : "rl-chip",
          `${id}-all-${destination.role}`,
          () => {
            allTo = destination.role;
            for (const holder of plan.holders) picks[holder.userId] = destination.role;
            draw();
          },
        );
        chips.append(chip);
      }
      chips.append(
        button("Choose for each person", "rl-chip", `${id}-choose-each`, () => {
          allTo = undefined;
          for (const holder of plan.holders) picks[holder.userId] = undefined;
          draw();
        }),
      );
      dialog.body.append(chips);

      dialog.body.append(
        section(`The ${plan.holders.length} ${plan.holders.length === 1 ? "person" : "people"}`),
      );
      for (const holder of sortByName(plan.holders, (h) => h.displayName)) {
        const row = el("div", "rl-person", `${id}-person`);
        row.dataset["userId"] = holder.userId;
        row.append(person(holder.displayName, false));
        const who = el("div", "rl-who");
        who.append(document.createTextNode(holder.displayName));
        who.append(el("small", "", undefined, holderNote(holder, plan.label)));
        row.append(who);
        const picked = picks[holder.userId];
        if (picked === undefined) {
          row.append(el("span", "rl-tag rl-bad", undefined, `Still has ${plan.label}`));
        } else {
          row.append(el("span", "rl-tag rl-good", undefined, `Moves to ${nameOf(picked)}`));
          row.append(
            button("Undo", "rl-btn rl-small", `${id}-undo`, () => {
              picks[holder.userId] = undefined;
              allTo = undefined;
              draw();
            }),
          );
        }
        if (picked === undefined) {
          const choices = el("div", "rl-chips rl-person-chips");
          for (const destination of sortByName(plan.destinations, (d) => d.label)) {
            if (destination.assignable) {
              choices.append(
                button(destination.label, "rl-chip", `${id}-pick-${destination.role}`, () => {
                  picks[holder.userId] = destination.role;
                  allTo = undefined;
                  draw();
                }),
              );
            } else {
              const dashed = el(
                "span",
                "rl-chip rl-dis rl-static",
                `${id}-cannot-give-${destination.role}`,
                `${destination.label} (not yours to give)`,
              );
              choices.append(dashed);
            }
          }
          row.append(choices);
        }
        dialog.body.append(row);
      }
    } else {
      dialog.body.append(
        el("p", "", `${id}-nobody`, `Nobody has ${plan.label}, so nobody is affected.`),
      );
    }
    dialog.body.append(
      el(
        "p",
        "rl-muted",
        undefined,
        `${plan.label} keeps its task list. You can switch it back on later, with nobody in it.`,
      ),
    );
    if (status !== "") {
      const err = el("p", "rl-err-text", `${id}-error`, status);
      dialog.body.append(err);
    }

    const note = el("p", "rl-note", `${id}-note`);
    if (plan.holders.length > 0) {
      const text = moveFooterText(
        left,
        plan.holders.length,
        everyoneGoesTo() === undefined ? undefined : nameOf(everyoneGoesTo() as string),
      );
      note.append(el("b", "", undefined, text.bold), document.createTextNode(text.rest));
    }
    confirm.textContent =
      plan.holders.length > 0 ? "Move and switch off" : `Switch off ${plan.label}`;
    confirm.disabled = left > 0;
    dialog.foot.append(note, cancelButton(id, "Cancel", close), confirm);
  };

  async function submit(): Promise<void> {
    const s = ctx().session();
    if (s === undefined) return;
    const gen = generation;
    confirm.disabled = true;
    const moves: Record<string, string> = {};
    for (const holder of plan.holders) {
      const destination = picks[holder.userId];
      if (destination !== undefined) moves[holder.userId] = destination;
    }
    const result = await retireTenantRole(
      s.apiBaseUrl,
      s.tenantId,
      role.role,
      plan.holders.length > 0 ? { moves } : undefined,
    );
    if (gen !== generation) return;
    if (result.status === 200) {
      dismiss(ROLE_HOST, dialog.sheet, false);
      const moved = result.body.movedUserIds.length;
      ctx().toast(
        moved > 0
          ? `${plan.label} is switched off. ${peopleCountText(moved)} moved first.`
          : `${plan.label} is switched off.`,
        "success",
      );
      await ctx().reload();
      return;
    }
    if (result.body.error === "role_held") {
      // Somebody was given the role while this was open: read the plan again.
      const fresh = await getRoleSwitchOffPlan(s.apiBaseUrl, s.tenantId, role.role);
      if (gen !== generation) return;
      if (fresh.status === 200) {
        renderSwitchOff(role, fresh.body);
        return;
      }
    }
    status = failureMessage(result, "Could not switch that role off.");
    draw();
  }

  draw();
  present(ROLE_HOST, dialog.sheet);
}

// ---------------------------------------------------------------------------------------------
// R6: the confirmation for a staged role edit
// ---------------------------------------------------------------------------------------------

export interface ReviewOutcome {
  kind: "saved" | "back";
  applied?: { permissionId: string; to: PermissionGrant }[];
}

/** The mandatory check before a staged role edit is saved. Resolves "back" when the person returns
 * to the editor (the staged changes are untouched) and "saved" once the server has applied them. */
export function reviewRoleSave(args: {
  role: string;
  roleLabel: string;
  changes: { permissionId: string; grant: PermissionGrant }[];
}): Promise<ReviewOutcome> {
  return new Promise<ReviewOutcome>((resolve) => {
    const id = "admin-role-review-sheet";
    let finished = false;
    const finish = (outcome: ReviewOutcome, backToEditor: boolean): void => {
      if (finished) return;
      finished = true;
      dismiss(ROLE_HOST, dialog.sheet, backToEditor);
      resolve(outcome);
    };
    const dialog = buildDialog(
      ROLE_HOST,
      id,
      "Check before you save",
      `Save changes to ${args.roleLabel}?`,
      680,
      () => finish({ kind: "back" }, true),
    );
    dialog.body.append(el("p", "rl-muted", `${id}-loading`, "Checking who this affects..."));
    present(ROLE_HOST, dialog.sheet);

    const s = ctx().session();
    if (s === undefined) {
      finish({ kind: "back" }, true);
      return;
    }
    const gen = generation;
    let expandedPeople = false;

    const load = async (): Promise<void> => {
      const result = await previewRolePermissionChanges(
        s.apiBaseUrl,
        s.tenantId,
        args.role,
        args.changes,
      );
      if (gen !== generation) return;
      if (result.status !== 200) {
        ctx().toast(failureMessage(result, "Could not check those changes."), "danger");
        finish({ kind: "back" }, true);
        return;
      }
      const preview = result.body;
      let count = preview.holders.length;

      const draw = (note?: string): void => {
        dialog.body.replaceChildren();
        dialog.foot.replaceChildren();
        if (note !== undefined) {
          // The people changed between the review and the save: nothing was saved, and one tap
          // on Save below saves for the list as it now stands.
          const stale = callout("bad", "⚠", "The people who have this role changed.", ` ${note}`);
          stale.dataset["testid"] = `${id}-stale`;
          dialog.body.append(stale);
        }
        const banner = saveBannerText(count);
        const box = callout("warn", "⚠", banner.bold, banner.rest);
        box.dataset["testid"] = `${id}-banner`;
        dialog.body.append(box);

        dialog.body.append(section("What changes"));
        for (const change of preview.changes) {
          const row = el("div", "rl-chg", `${id}-change`);
          row.dataset["permissionId"] = change.permissionId;
          const kind =
            change.direction === "widen" ? "up" : change.direction === "narrow" ? "down" : "mid";
          row.append(el("span", `rl-sym rl-${kind}`, undefined, CHANGE_SYMBOLS[change.direction]));
          const tx = el("div", "rl-tx");
          tx.append(
            el("b", "", undefined, change.label),
            el("small", "", undefined, changeLine(change.from, change.to)),
          );
          row.append(tx);
          dialog.body.append(row);
        }

        if (count > 0) {
          dialog.body.append(section(`Who is affected (${count})`));
          const chips = el("div", "rl-chips", `${id}-people`);
          const visible = expandedPeople
            ? preview.holders
            : preview.holders.slice(0, PEOPLE_CHIPS_VISIBLE);
          for (const holder of visible) {
            const chip = el("span", "rl-chip rl-static", `${id}-person`);
            chip.append(
              person(holder.displayName, true),
              document.createTextNode(holder.displayName),
            );
            chips.append(chip);
          }
          if (!expandedPeople && count > PEOPLE_CHIPS_VISIBLE) {
            chips.append(
              button(`Show all ${count}`, "rl-chip", `${id}-show-all`, () => {
                expandedPeople = true;
                draw(note);
              }),
            );
          }
          dialog.body.append(chips);
        }
        const personal = personalSettingsNote(preview.personalSettingsStillWin);
        if (personal !== null) {
          dialog.body.append(el("p", "rl-muted", `${id}-personal`, personal));
        }

        const footNote = el("p", "rl-note");
        footNote.append(
          el(
            "b",
            "",
            undefined,
            count === 0 ? "Applies to nobody yet" : `Applies to ${peopleCountText(count)}`,
          ),
        );
        const save = button(
          saveButtonText(count),
          "rl-btn rl-primary",
          `${id}-save`,
          () => void commit(save),
        );
        dialog.foot.append(
          footNote,
          button("Go back", "rl-btn", `${id}-back`, () => finish({ kind: "back" }, true)),
          save,
        );
      };

      const commit = async (save: HTMLButtonElement): Promise<void> => {
        save.disabled = true;
        const saved = await saveRolePermissions(
          s.apiBaseUrl,
          s.tenantId,
          args.role,
          args.changes,
          count,
        );
        if (gen !== generation) return;
        if (saved.status === 200) {
          ctx().toast(
            saved.body.affectedCount === 0
              ? `${args.roleLabel} saved.`
              : `${args.roleLabel} saved for ${peopleCountText(saved.body.affectedCount)}.`,
            "success",
          );
          finish(
            {
              kind: "saved",
              applied: saved.body.applied.map((a) => ({ permissionId: a.permissionId, to: a.to })),
            },
            true,
          );
          return;
        }
        if (saved.body.error === "confirmation_stale") {
          // The people who hold the role changed while this was open: show the new list and ask again.
          const fresh = await previewRolePermissionChanges(
            s.apiBaseUrl,
            s.tenantId,
            args.role,
            args.changes,
          );
          if (gen !== generation) return;
          if (fresh.status === 200) {
            preview.holders = fresh.body.holders;
            preview.personalSettingsStillWin = fresh.body.personalSettingsStillWin;
            preview.changes = fresh.body.changes;
            count = fresh.body.holders.length;
            draw(
              `Nothing was saved. It now affects ${peopleCountText(count)}. Check the list below, then tap Save.`,
            );
            return;
          }
        }
        ctx().toast(failureMessage(saved, "Could not save those changes."), "danger");
        finish({ kind: "back" }, true);
      };
      draw();
    };
    void load();
  });
}

// ---------------------------------------------------------------------------------------------
// R6: adding a role to a person, previewed
// ---------------------------------------------------------------------------------------------

/** Shows what adding the role would let this person do, and resolves true when the person confirms. */
export function confirmAddRole(args: { user: AdminUserDto; role: string }): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const id = "admin-role-add-preview-sheet";
    let finished = false;
    const finish = (answer: boolean): void => {
      if (finished) return;
      finished = true;
      dismiss(USER_HOST, dialog.sheet, true);
      resolve(answer);
    };
    const roleName = labelOf(args.role);
    const dialog = buildDialog(
      USER_HOST,
      id,
      "Add a role",
      `Give ${args.user.displayName} the ${roleName} role?`,
      660,
      () => finish(false),
    );
    dialog.body.append(el("p", "rl-muted", `${id}-loading`, "Checking what this adds..."));
    present(USER_HOST, dialog.sheet);
    const s = ctx().session();
    if (s === undefined) {
      finish(false);
      return;
    }
    const gen = generation;
    void (async () => {
      const result = await previewAddRoleToUser(s.apiBaseUrl, s.tenantId, args.user.id, args.role);
      if (gen !== generation) return;
      if (result.status !== 200) {
        ctx().toast(failureMessage(result, "Could not check that role."), "danger");
        finish(false);
        return;
      }
      const preview = result.body;
      let showAll = false;
      const draw = (): void => {
        dialog.body.replaceChildren();
        dialog.foot.replaceChildren();
        const intro = addRoleIntro(preview.displayName, preview.gained.length);
        const p = el("p", "", `${id}-intro`);
        p.append(
          document.createTextNode(intro.lead),
          el("b", "", undefined, intro.bold),
          document.createTextNode(intro.tail),
        );
        dialog.body.append(p);
        const visible = showAll ? preview.gained : preview.gained.slice(0, 4);
        for (const gain of visible) {
          const row = el("div", "rl-chg", `${id}-gain`);
          row.dataset["permissionId"] = gain.permissionId;
          row.append(el("span", "rl-sym rl-up", undefined, "+"));
          const tx = el("div", "rl-tx");
          tx.append(
            el("b", "", undefined, gain.label),
            el("small", "", undefined, gainLine(gain.now, gain.withRole, preview.addRoleLabel)),
          );
          row.append(tx);
          dialog.body.append(row);
        }
        if (!showAll && preview.gained.length > 4) {
          const wrap = el("div", "rl-chips");
          wrap.style.margin = "var(--space-2) 0 var(--space-3)";
          wrap.append(
            button(`Show all ${preview.gained.length}`, "rl-btn rl-small", `${id}-show-all`, () => {
              showAll = true;
              draw();
            }),
          );
          dialog.body.append(wrap);
        }
        const wins = personalWinsNote(preview.displayName, preview.personalSettingsStillWin);
        if (wins !== null) {
          const box = callout("", "ⓘ", "", "");
          const text = box.lastElementChild as HTMLElement;
          text.append(
            document.createTextNode(wins.lead),
            el("b", "", undefined, wins.bold),
            document.createTextNode(wins.tail),
          );
          box.dataset["testid"] = `${id}-personal`;
          dialog.body.append(box);
        }
        dialog.body.append(
          el(
            "p",
            "rl-muted",
            undefined,
            `Adding a role never takes anything away. To remove access later, take the role off ${firstName(preview.displayName)} or change the role itself.`,
          ),
        );
        if (!preview.actorMayAssign) {
          const box = callout(
            "warn",
            "⚠",
            "You cannot give this role out.",
            ` It holds access you do not have yourself. Ask an Admin who has it to make the change.`,
          );
          box.dataset["testid"] = `${id}-cap`;
          dialog.body.append(box);
        }
        const note = el("p", "rl-note");
        note.append(
          el("b", "", undefined, preview.displayName),
          document.createTextNode(
            `: ${[...preview.currentRoles.map((r) => r.label), preview.addRoleLabel].join(" + ")}`,
          ),
        );
        const add = button(
          `Add ${preview.addRoleLabel}`,
          "rl-btn rl-primary",
          `${id}-confirm`,
          () => finish(true),
        );
        add.disabled = !preview.actorMayAssign;
        dialog.foot.append(
          note,
          button("Do not add", "rl-btn", `${id}-cancel`, () => finish(false)),
          add,
        );
      };
      draw();
    })();
  });
}

// ---------------------------------------------------------------------------------------------
// R7: the Access readout
// ---------------------------------------------------------------------------------------------

export interface AccessReadoutArgs {
  person: AdminUserDto;
  rows: readonly UserPermissionRowDto[];
  /** Areas the person has opened with "Show details". */
  openAreas: Set<string>;
  onUseRoleSetting(row: UserPermissionRowDto): void;
  onAddException(): void;
  onToggleArea(group: string): void;
  rolesHeld: number;
}

export function renderAccessReadout(root: HTMLElement, args: AccessReadoutArgs): void {
  root.replaceChildren();
  const who = firstName(args.person.displayName);
  const labelFor = (role: string): string => labelOf(role);

  root.append(section(`What ${who} can do`));
  const counts = accessCounts(args.rows);
  const tiles = el("div", "rl-tiles", "admin-access-tiles");
  const tile = (kind: string, n: number, label: string, testId: string): HTMLElement => {
    const t = el("div", `rl-tile rl-${kind}`, testId);
    t.append(
      el("div", "rl-tile-n", undefined, String(n)),
      el("div", "rl-tile-k", undefined, label),
    );
    return t;
  };
  tiles.append(
    tile("g", counts.alone, `On their own`, "admin-access-tile-alone"),
    tile("a", counts.withApproval, "With a manager's OK", "admin-access-tile-approval"),
    tile("r", counts.notAllowed, "Not allowed", "admin-access-tile-denied"),
  );
  root.append(tiles);

  const exceptions = exceptionRows(args.rows);
  if (exceptions.length > 0) {
    root.append(section(`Changed just for ${who} (${exceptions.length})`));
    for (const row of exceptions) {
      const card = el("div", "rl-excard", "admin-access-exception");
      card.dataset["permissionId"] = row.permissionId;
      const top = el("div", "rl-excard-top");
      top.append(
        el("b", "", undefined, row.label),
        el("span", "rl-tag rl-you", undefined, `Just for ${who}`),
      );
      const arrow = el("div", "rl-arrow");
      const mine = row.override ?? row.effectiveGrant;
      arrow.append(
        el(
          "span",
          "rl-tag rl-role",
          `admin-access-role-says`,
          roleSaysText(row, labelFor, args.rolesHeld),
        ),
        el("span", "", undefined, "→"),
        el(
          "span",
          mine === "allowed"
            ? "rl-tag rl-good"
            : mine === "denied"
              ? "rl-tag rl-bad"
              : "rl-tag rl-warn",
          "admin-access-person-says",
          `${who}: ${grantWord(mine)}`,
        ),
      );
      const useRole = button(
        "Use the role setting",
        "rl-btn rl-small",
        "admin-access-use-role",
        () => args.onUseRoleSetting(row),
      );
      useRole.dataset["permissionId"] = row.permissionId;
      card.append(top, arrow, useRole);
      root.append(card);
    }
  }
  const add = button(`+ Change one thing for ${who}`, "rl-btn", "admin-access-add-exception", () =>
    args.onAddException(),
  );
  add.style.marginTop = "var(--space-2)";
  root.append(add);

  root.append(section(`Everything ${who} can do, by area`));
  const card = el("div", "rl-opt-list", "admin-access-areas");
  card.style.padding = "0 var(--space-4)";
  for (const area of areaSummaries(args.rows, args.person.displayName, labelFor)) {
    const wrap = el("div", "rl-area", "admin-access-area");
    wrap.dataset["group"] = area.group;
    const head = el("div", "rl-area-head");
    head.append(el("b", "", undefined, area.groupLabel));
    if (area.headline === null) {
      head.append(el("span", "rl-muted", undefined, "Nothing allowed"));
    } else {
      head.append(
        el(
          "span",
          area.headline.individual ? "rl-tag rl-you" : "rl-tag rl-role",
          undefined,
          area.headline.text,
        ),
      );
    }
    const open = args.openAreas.has(area.group);
    if (area.lines.length > 0) {
      const more = button(
        open ? "Hide details" : "Show details",
        "rl-btn rl-small rl-more",
        "admin-access-area-toggle",
        () => args.onToggleArea(area.group),
      );
      more.dataset["group"] = area.group;
      more.setAttribute("aria-expanded", String(open));
      head.append(more);
    }
    wrap.append(head);
    if (open) {
      for (const row of area.lines) {
        const line = el("div", "rl-chg", "admin-access-line");
        line.dataset["permissionId"] = row.permissionId;
        const ask = row.effectiveGrant === "approval_required";
        line.append(el("span", `rl-sym ${ask ? "rl-mid" : "rl-up"}`, undefined, ask ? "!" : "✓"));
        const tx = el("div", "rl-tx");
        tx.append(
          document.createTextNode(ask ? `${row.label}, with a manager's OK ` : `${row.label} `),
        );
        const badge = sourceBadge(row, args.person.displayName, labelFor);
        tx.append(
          el(
            "span",
            badge.individual ? "rl-tag rl-you" : "rl-tag rl-role",
            "admin-access-badge",
            badge.text,
          ),
        );
        line.append(tx);
        wrap.append(line);
      }
    }
    card.append(wrap);
  }
  root.append(card);
}

// ---------------------------------------------------------------------------------------------
// R7: change one thing for one person
// ---------------------------------------------------------------------------------------------

export interface ExceptionChoice {
  permissionId: string;
  grant: PermissionGrant;
}

export function pickException(args: {
  person: AdminUserDto;
  rows: readonly UserPermissionRowDto[];
}): Promise<ExceptionChoice | undefined> {
  return new Promise((resolve) => {
    const id = "admin-access-exception-sheet";
    let finished = false;
    const finish = (answer: ExceptionChoice | undefined): void => {
      if (finished) return;
      finished = true;
      dismiss(USER_HOST, dialog.sheet, true);
      resolve(answer);
    };
    const who = firstName(args.person.displayName);
    const dialog = buildDialog(
      USER_HOST,
      id,
      args.person.displayName,
      `Change one thing for ${who}`,
      680,
      () => finish(undefined),
    );
    const usable = args.rows.filter((row) => !row.planned);
    // PERMSEARCH1: the areas are the Settings menu's sections (then Clients and Reports), in that order.
    const sections = searchPermissionSections(usable, "");
    const groups: { key: string; label: string }[] = sections.map((sec) => ({
      key: sec.key,
      label: sec.label,
    }));
    let group = groups[0]?.key;
    let task: string | undefined;
    let choice: PermissionGrant | undefined;
    const searchInput = el("input", "search-input", `${id}-search`);
    searchInput.type = "search";
    searchInput.placeholder = "Search permissions";
    searchInput.setAttribute("aria-label", "Search permissions");
    searchInput.autocomplete = "off";
    searchInput.spellcheck = false;
    searchInput.dataset["settingExempt"] = "list tool";
    searchInput.addEventListener("input", () => {
      draw();
      searchInput.focus();
      const end = searchInput.value.length;
      searchInput.setSelectionRange(end, end);
    });
    const withMarks = (parent: HTMLElement, text: string, query: string): void => {
      const parts = splitForHighlight(text, query);
      if (parts === null) {
        parent.append(document.createTextNode(text));
        return;
      }
      const mark = document.createElement("mark");
      mark.textContent = parts.match;
      parent.append(
        document.createTextNode(parts.before),
        mark,
        document.createTextNode(parts.after),
      );
    };

    const draw = (): void => {
      dialog.body.replaceChildren();
      dialog.foot.replaceChildren();
      const query = searchInput.value;
      const searching = query.trim() !== "";
      const found = searchPermissionSections(usable, query);
      const first = section("1. Pick the task");
      first.style.marginTop = "0";
      dialog.body.append(first);
      const searchBlock = el("div", "rl-search-block");
      searchBlock.append(
        searchInput,
        el(
          "p",
          "rl-search-count",
          `${id}-search-count`,
          searching && found.length === 0
            ? noMatchText(query).headline
            : permissionCountText(found, searching),
        ),
      );
      dialog.body.append(searchBlock);
      const chips = el("div", "rl-chips");
      chips.style.marginBottom = "var(--space-3)";
      if (!searching) {
        for (const g of groups) {
          chips.append(
            button(g.label, g.key === group ? "rl-chip rl-on" : "rl-chip", `${id}-area`, () => {
              group = g.key;
              task = undefined;
              choice = undefined;
              draw();
            }),
          );
        }
        dialog.body.append(chips);
      }
      const list = el("div", "rl-task-list", `${id}-tasks`);
      // The area chips and step labels stay in view: only the task list scrolls, and the picked
      // task is scrolled into view.
      list.style.maxHeight = "420px";
      list.style.overflowY = "auto";
      let pickedItem: HTMLElement | undefined;
      let selectedWrap: HTMLElement | undefined;
      const selected = usable.find((row) => row.permissionId === task);
      // Browsing shows the picked area; searching shows every section that has a match.
      const shown = searching ? found : found.filter((sec) => sec.key === group);
      let outsideLabelShown = false;
      for (const sec of shown) {
        if (searching) {
          if (sec.outsideSettings && !outsideLabelShown) {
            outsideLabelShown = true;
            list.append(el("div", "rl-task-section", undefined, "Outside the Settings menu"));
          }
          list.append(el("div", "rl-task-section", `${id}-section`, sec.label));
        }
        for (const hit of sec.hits) {
          const row = hit.row;
          const item = el(
            "button",
            row.permissionId === task ? "rl-task rl-on" : "rl-task",
            `${id}-task`,
          );
          item.type = "button";
          item.dataset["permissionId"] = row.permissionId;
          const text = el("span", "rl-task-text");
          const name = el("span");
          withMarks(name, row.label, query);
          text.append(name);
          const description = PERMISSION_DESCRIPTIONS[row.permissionId];
          if (description !== undefined) {
            const sub = el("span", "rl-task-sub");
            withMarks(sub, description, query);
            text.append(sub);
          }
          if (hit.why !== null) text.append(el("span", "rl-task-sub", undefined, hit.why));
          item.append(text);
          if (row.override !== null)
            item.append(el("span", "rl-tag rl-you", undefined, "Already changed"));
          const state = el("span", "rl-task-state");
          state.append(el("span", "", undefined, grantWord(row.effectiveGrant)));
          state.append(
            el("span", "", undefined, sourceBadge(row, args.person.displayName, labelOf).text),
          );
          item.append(state);
          if (row.permissionId === task) {
            item.append(el("span", "", undefined, "\u2713"));
            pickedItem = item;
          }
          item.addEventListener("click", () => {
            task = row.permissionId;
            choice = undefined;
            draw();
          });
          const wrap = el("div", "rl-task-row");
          wrap.append(item);
          if (row.permissionId === task) selectedWrap = wrap;
          list.append(wrap);
        }
      }
      dialog.body.append(list);
      const scrollPicked = pickedItem;
      requestAnimationFrame(() => scrollPicked?.scrollIntoView({ block: "nearest" }));

      if (selected !== undefined) {
        const editor = el("div", "rl-inline-editor", `${id}-editor`);
        const seg = el("div", "rl-seg", `${id}-choices`);
        const useRole = button(
          `Use role (${grantWord(selected.roleGrant)})`,
          `rl-seg-opt${choice === undefined && selected.override === null ? " rl-on" : ""}`,
          `${id}-choice-role`,
          () => {
            choice = undefined;
            draw();
          },
        );
        seg.append(useRole);
        const blocked: string[] = [];
        for (const option of exceptionChoices(selected)) {
          const block = exceptionChoiceBlock(selected, option);
          const cls =
            option === "allowed"
              ? "rl-allow"
              : option === "approval_required"
                ? "rl-ask"
                : "rl-deny";
          const b = el(
            "button",
            `rl-seg-opt ${cls}${choice === option || (choice === undefined && option === selected.override) ? " rl-on" : ""}${option === selected.roleGrant ? " rl-roles-own" : ""}`,
            `${id}-choice-${option}`,
          );
          b.append(el("span", "", undefined, grantWord(option)));
          if (option === selected.roleGrant) {
            // The role's own value: a thin neutral outline and a caption, never a fill.
            const owners = roleNamesFor(selected, (r) => labelOf(r));
            b.append(
              el(
                "small",
                "rl-own-caption",
                `${id}-own-caption`,
                owners.length === 0 ? "Role says" : `${owners} says`,
              ),
            );
          }
          b.type = "button";
          b.disabled = block !== null;
          if (block !== null) blocked.push(`${grantWord(option)}: ${block}`);
          b.addEventListener("click", () => {
            choice = option;
            draw();
          });
          seg.append(b);
        }
        editor.append(seg);
        const says = el("p", "rl-muted", `${id}-role-says`);
        says.style.marginTop = "var(--space-2)";
        const heldBy = roleNamesFor(selected, (r) => labelOf(r));
        says.append(
          document.createTextNode(
            `${heldBy.length === 0 ? "Their role" : `The ${heldBy} ${selected.roleGrantFrom.length === 1 ? "role" : "roles"}`} ${selected.roleGrantFrom.length === 1 || heldBy.length === 0 ? "says" : "say"} `,
          ),
          el("b", "", undefined, grantWord(selected.roleGrant)),
          document.createTextNode(". Pick something different to make an exception."),
        );
        editor.append(says);
        if (blocked.length > 0) {
          editor.append(el("p", "rl-muted", `${id}-blocked`, blocked[0]));
        }
        const others = selected.roleGrantFrom
          .flatMap((r) =>
            ctx()
              .users()
              .filter(
                (u) => u.active && u.id !== args.person.id && (u.roles ?? [u.role]).includes(r),
              ),
          )
          .filter((u, i, all) => all.findIndex((x) => x.id === u.id) === i).length;
        editor.append(
          callout(
            "",
            "ⓘ",
            "",
            `This only changes ${who}. ${others === 0 ? "Nobody else is affected." : others === 1 ? "The 1 other person who has this role is not affected." : `The ${others} other people who have this role are not affected.`}`,
          ),
        );
        selectedWrap?.append(editor);
      }

      const ready = selected !== undefined && exceptionSaveReady(selected, choice);
      dialog.foot.append(
        el(
          "p",
          "rl-note",
          `${id}-note`,
          ready ? `Saves for ${who} only.` : "Pick a setting that is not the role's to continue.",
        ),
        cancelButton(id, "Cancel", () => finish(undefined)),
      );
      const save = button(
        searching ? `Save change for ${who}` : `Save for ${who}`,
        "rl-btn rl-primary",
        `${id}-save`,
        () => {
          if (selected !== undefined && choice !== undefined) {
            finish({ permissionId: selected.permissionId, grant: choice });
          }
        },
      );
      save.disabled = !ready;
      dialog.foot.append(save);
    };
    draw();
    present(USER_HOST, dialog.sheet);
  });
}

// ---------------------------------------------------------------------------------------------
// Teardown
// ---------------------------------------------------------------------------------------------

const OWN_SHEET_IDS = [
  "admin-role-options-sheet",
  "admin-role-rename-sheet",
  "admin-role-copy-sheet",
  "admin-role-switchoff-sheet",
  "admin-role-review-sheet",
  "admin-role-add-preview-sheet",
  "admin-access-exception-sheet",
];

/** Closes every dialog this module opened and releases the scroll lock it holds. Called by the
 * Settings teardown BEFORE it closes the sheets underneath, so a lock is released once. */
export function dismissRolesManagementSheets(): void {
  generation += 1;
  for (const id of OWN_SHEET_IDS) {
    const sheet = document.getElementById(id);
    if (sheet !== null) sheet.hidden = true;
  }
  for (const host of [ROLE_HOST, USER_HOST]) {
    const scrim = scrimOf(host);
    if (scrim === null) continue;
    scrim.classList.remove("rl-centered");
    if (ownsLock.get(host.scrimId) === true) {
      ownsLock.set(host.scrimId, false);
      scrim.hidden = true;
      scrim.classList.remove("rl-drawer");
      ctx().unlockScroll();
    }
  }
}

export function resetRolesManagement(): void {
  dismissRolesManagementSheets();
  for (const id of OWN_SHEET_IDS) {
    document.getElementById(id)?.remove();
  }
}
